"use strict";
/**
 * Thread local cache (person-scoped localStorage).
 *
 * Durable many-to-many cross-links (invoice / epic / phase / box / reflection).
 * Not timestream (time flow) and not pnParentFK (tree).
 *
 * Server (Python /api/v2/threads) is source of truth; this module is the
 * write-through cache for pills and digest-stable ng-repeat lists.
 * Shapes match audroam-api thread / thread_member (camelCase JSON).
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.threadsForRow = exports.listMembershipsForRow = exports.listMembers = exports.deleteThread = exports.updateThread = exports.createThread = exports.getThread = exports.listThreads = exports.upsertLocalMember = exports.replaceLocalMembersForThread = exports.upsertLocalThread = exports.replacePersonThreadCache = exports.mergeMemberPathHints = exports.preferMemberPathHint = exports.isGenericPathHint = exports.normalizeMemberRecord = exports.normalizeThreadRecord = exports.normalizeMemberId = exports.memberEffectiveStatus = exports.nextMemberCheckRole = exports.memberCheckTitle = exports.memberCheckMark = exports.memberCheckState = exports.memberProgressSectionIdForStatus = exports.memberProgressOption = exports.defaultMemberStatusForKind = exports.defaultMemberRoleForKind = exports.threadStatusPillClass = exports.threadStatusLabel = exports.threadPillLabel = exports.threadCaption = exports.parseThreadsPath = exports.isThreadViewUrl = exports.threadIdFromViewUrl = exports.isThreadsListUrl = exports.threadViewUrl = exports.threadsListUrl = exports.threadMembersStorageKey = exports.threadsStorageKey = exports.THREAD_KINDS = exports.WORK_LIST_THREAD_KINDS = exports.MEMBER_CHECK_CYCLE = exports.isMemberProgressStatus = exports.MEMBER_PROGRESS_SECTIONS = exports.MEMBER_PROGRESS_OPTIONS = exports.THREAD_VIEW_URL_PREFIX_LEGACY = exports.THREAD_VIEW_URL_PREFIX = exports.THREADS_LIST_URL = exports.THREAD_MEMBERS_STORAGE_PREFIX = exports.THREADS_STORAGE_PREFIX = void 0;
exports.recentThreadsForAdd = exports.attachThreadPillsToRows = exports.pillsForRow = exports.removeMember = exports.updateMember = exports.addMember = exports.isMemberOfThread = void 0;
exports.THREADS_STORAGE_PREFIX = 'audroam.threads';
exports.THREAD_MEMBERS_STORAGE_PREFIX = 'audroam.threadMembers';
/** First-class thread list + detail URLs. */
exports.THREADS_LIST_URL = '/threads';
exports.THREAD_VIEW_URL_PREFIX = '/threads/';
/** Older view-history prefix; still parsed. */
exports.THREAD_VIEW_URL_PREFIX_LEGACY = '/view/thread/';
/**
 * Progress + invoice role choices for thread membership (pill dropdown).
 * Progress/meta/pct → `thread_member.status`; invoice → `thread_member.role`.
 */
exports.MEMBER_PROGRESS_OPTIONS = [
    // Plan & start
    { role: 'hidden', label: 'Hide progress', mark: '', group: 'meta' },
    { role: 'todo', label: 'To do (0%)', mark: '0%', group: 'plan' },
    { role: 'planning', label: 'Planning', mark: 'plan', group: 'plan' },
    { role: 'brainstorming', label: 'Brainstorming', mark: 'idea', group: 'plan', alsoIn: ['make'] },
    { role: 'research', label: 'Research', mark: 'res', group: 'plan', alsoIn: ['make'] },
    { role: 'started', label: 'Started', mark: 'go', group: 'plan' },
    // Build (also listed under Plan & start)
    { role: 'doing', label: 'In progress', mark: '…', group: 'build', alsoIn: ['plan'] },
    {
        role: 'documenting',
        label: 'Documenting',
        mark: 'doc',
        group: 'build',
        alsoIn: ['plan', 'make'],
    },
    // Make / studio (sculpture + physical)
    { role: 'sketching', label: 'Sketching', mark: 'sk', group: 'make', alsoIn: ['plan'] },
    { role: 'prototyping', label: 'Prototyping', mark: 'prt', group: 'make', alsoIn: ['build'] },
    { role: 'cataloguing', label: 'Cataloguing', mark: 'cat', group: 'make' },
    // Test (also listed under Follow-up)
    {
        role: 'testing_local',
        label: 'Testing locally',
        mark: 'loc',
        group: 'verify',
        alsoIn: ['followup'],
    },
    {
        role: 'testing_internal',
        label: 'Internal testing',
        mark: 'int',
        group: 'verify',
        alsoIn: ['followup'],
    },
    {
        role: 'burn_in',
        label: 'Burn-in testing',
        mark: 'burn',
        group: 'verify',
        alsoIn: ['make', 'followup'],
    },
    { role: 'review', label: 'In review', mark: 'rev', group: 'verify', alsoIn: ['make'] },
    // Deploy (also listed under Follow-up)
    { role: 'deploying', label: 'Deploying', mark: 'dep', group: 'ship', alsoIn: ['followup'] },
    {
        role: 'deployed_feedback',
        label: 'Deployed for feedback',
        mark: 'out',
        group: 'ship',
        alsoIn: ['followup'],
    },
    { role: 'printing', label: 'Printing', mark: 'prn', group: 'ship', alsoIn: ['make'] },
    { role: 'install', label: 'Installing', mark: 'inst', group: 'ship', alsoIn: ['make'] },
    { role: 'exhibiting', label: 'Exhibiting', mark: 'exh', group: 'ship', alsoIn: ['make'] },
    // Finish
    {
        role: 'polish',
        // finish off / commit / clean up / refactor
        label: 'Polish & wrap-up',
        mark: 'pol',
        group: 'close',
        alsoIn: ['make'],
    },
    // Follow-up
    {
        role: 'stability',
        // reword of former "Bugfix"
        label: 'Needs stability work',
        mark: 'stab',
        group: 'followup',
    },
    { role: 'feedback', label: 'Feedback received', mark: 'fb', group: 'followup' },
    {
        role: 'ongoing',
        label: 'Ongoing / housekeeping',
        mark: 'loop',
        group: 'followup',
        alsoIn: ['plan'],
    },
    {
        role: 'monitoring',
        label: 'Monitoring (periodic checks)',
        mark: 'mon',
        group: 'followup',
        alsoIn: ['verify'],
    },
    { role: 'warranty', label: 'Warranty', mark: 'war', group: 'followup', alsoIn: ['make'] },
    { role: 'waiting', label: 'Waiting / blocked', mark: 'wait', group: 'followup', alsoIn: ['plan'] },
    { role: 'paused', label: 'Paused', mark: '||', group: 'followup', alsoIn: ['plan'] },
    // Percent (coarse completion, orthogonal to stage labels)
    { role: 'pct10', label: '10%', mark: '10%', group: 'pct', alsoIn: ['plan'] },
    { role: 'pct25', label: '25%', mark: '25%', group: 'pct', alsoIn: ['plan'] },
    { role: 'pct50', label: '50%', mark: '50%', group: 'pct' },
    { role: 'pct75', label: '75%', mark: '75%', group: 'pct' },
    { role: 'pct90', label: '90%', mark: '90%', group: 'pct' },
    { role: 'done', label: 'Completed', mark: '✓', group: 'close', alsoIn: ['pct'] },
    // Invoice / link role
    { role: 'line_item', label: 'Line item', mark: '', group: 'invoice' },
    { role: 'evidence', label: 'Evidence', mark: 'ev', group: 'invoice' },
    { role: 'related', label: 'Related', mark: 'rel', group: 'invoice' },
    { role: 'primary', label: 'Primary', mark: '★', group: 'invoice' },
];
var AXIS_LABEL = {
    phase: 'By phase',
    theme: 'By theme',
    meta: 'Link',
};
/** Accordion sections: phases first (timeline), then themes (cross-cutting). */
var MEMBER_PROGRESS_SECTION_DEFS = [
    { id: 'plan', label: 'Plan & start', groups: ['meta', 'plan'], axis: 'phase' },
    { id: 'build', label: 'Build', groups: ['build'], axis: 'phase' },
    { id: 'verify', label: 'Test', groups: ['verify'], axis: 'phase' },
    { id: 'ship', label: 'Deploy', groups: ['ship'], axis: 'phase' },
    { id: 'close', label: 'Finish', groups: ['close'], axis: 'phase' },
    { id: 'make', label: 'Make', groups: ['make'], axis: 'theme' },
    { id: 'followup', label: 'Follow-up', groups: ['followup'], axis: 'theme' },
    { id: 'pct', label: 'Percent', groups: ['pct'], axis: 'theme' },
    { id: 'invoice', label: 'Invoice role', groups: ['invoice'], axis: 'meta', isRole: true },
];
function optionGroups(o) {
    return o.alsoIn && o.alsoIn.length ? __spreadArray([o.group], o.alsoIn, true) : [o.group];
}
exports.MEMBER_PROGRESS_SECTIONS = MEMBER_PROGRESS_SECTION_DEFS.map(function (sec, i, all) { return (__assign(__assign({}, sec), { axisLabel: i === 0 || all[i - 1].axis !== sec.axis ? AXIS_LABEL[sec.axis] : undefined, options: exports.MEMBER_PROGRESS_OPTIONS.filter(function (o) {
        return optionGroups(o).some(function (g) { return sec.groups.includes(g); });
    }).sort(function (a, b) {
        var aPri = sec.groups.includes(a.group) ? 0 : 1;
        var bPri = sec.groups.includes(b.group) ? 0 : 1;
        return aPri - bPri;
    }) })); });
var PROGRESS_BY_VALUE = new Map(exports.MEMBER_PROGRESS_OPTIONS.map(function (o) { return [o.role, o]; }));
var PROGRESS_STATUS_VALUES = new Set(exports.MEMBER_PROGRESS_OPTIONS.filter(function (o) { return o.group !== 'invoice'; }).map(function (o) { return o.role; }));
var INVOICE_ROLE_VALUES = new Set(exports.MEMBER_PROGRESS_OPTIONS.filter(function (o) { return o.group === 'invoice'; }).map(function (o) { return o.role; }));
/** True if value is membership progress (lives in status column). */
function isMemberProgressStatus(value) {
    var r = (value || '').trim().toLowerCase();
    if (!r || r === '__removed__')
        return false;
    if (INVOICE_ROLE_VALUES.has(r))
        return false;
    if (PROGRESS_STATUS_VALUES.has(r))
        return true;
    // Free-form / aliases still treat as status when not invoice
    return !INVOICE_ROLE_VALUES.has(r);
}
exports.isMemberProgressStatus = isMemberProgressStatus;
exports.MEMBER_CHECK_CYCLE = [
    'todo',
    'planning',
    'started',
    'doing',
    'testing_local',
    'testing_internal',
    'deploying',
    'deployed_feedback',
    'polish',
    'done',
];
/** Thread kinds that default new members to todo (work list) rather than line_item. */
exports.WORK_LIST_THREAD_KINDS = [
    'epic',
    'phase',
    'topic',
    'custom',
];
exports.THREAD_KINDS = [
    'invoice',
    'epic',
    'phase',
    'box',
    'reflection',
    'topic',
    'matter',
    'custom',
];
function threadsStorageKey(personId) {
    return "".concat(exports.THREADS_STORAGE_PREFIX, ":").concat(personId !== null && personId !== void 0 ? personId : 'anon');
}
exports.threadsStorageKey = threadsStorageKey;
function threadMembersStorageKey(personId) {
    return "".concat(exports.THREAD_MEMBERS_STORAGE_PREFIX, ":").concat(personId !== null && personId !== void 0 ? personId : 'anon');
}
exports.threadMembersStorageKey = threadMembersStorageKey;
function threadsListUrl() {
    return exports.THREADS_LIST_URL;
}
exports.threadsListUrl = threadsListUrl;
function threadViewUrl(threadId) {
    return "".concat(exports.THREAD_VIEW_URL_PREFIX).concat(encodeURIComponent(threadId));
}
exports.threadViewUrl = threadViewUrl;
function pathnameOf(url) {
    if (!url || typeof url !== 'string')
        return '';
    var noHash = url.split('#')[0];
    var path = noHash.split('?')[0];
    return path || '';
}
function isThreadsListUrl(url) {
    var path = pathnameOf(url);
    return path === exports.THREADS_LIST_URL || path === "".concat(exports.THREADS_LIST_URL, "/");
}
exports.isThreadsListUrl = isThreadsListUrl;
function threadIdFromViewUrl(url) {
    var path = pathnameOf(url);
    for (var _i = 0, _a = [exports.THREAD_VIEW_URL_PREFIX, exports.THREAD_VIEW_URL_PREFIX_LEGACY]; _i < _a.length; _i++) {
        var prefix = _a[_i];
        if (path.startsWith(prefix)) {
            var raw = path.slice(prefix.length);
            if (!raw)
                return null;
            try {
                return decodeURIComponent(raw);
            }
            catch (_b) {
                return raw;
            }
        }
    }
    return null;
}
exports.threadIdFromViewUrl = threadIdFromViewUrl;
function isThreadViewUrl(url) {
    return threadIdFromViewUrl(url) != null;
}
exports.isThreadViewUrl = isThreadViewUrl;
/** Parse /threads, /threads/:id, or legacy /view/thread/:id. */
function parseThreadsPath(pathname) {
    var path = pathnameOf(pathname);
    if (isThreadsListUrl(path)) {
        return { list: true, threadId: null };
    }
    var threadId = threadIdFromViewUrl(path);
    return { list: false, threadId: threadId };
}
exports.parseThreadsPath = parseThreadsPath;
function threadCaption(thread) {
    var kind = (thread.kind || 'custom').trim() || 'custom';
    var title = (thread.title || '').trim();
    return title ? "Thread \u00B7 ".concat(kind, " \u2014 ").concat(title) : "Thread \u00B7 ".concat(kind);
}
exports.threadCaption = threadCaption;
function threadPillLabel(thread) {
    var kind = (thread.kind || 'custom').trim() || 'custom';
    var title = (thread.title || '').trim() || 'untitled';
    return "".concat(kind, " \u00B7 ").concat(title);
}
exports.threadPillLabel = threadPillLabel;
/** Display label for thread open/closed (and free-form) status pills. */
function threadStatusLabel(status) {
    var s = (status || 'open').trim() || 'open';
    var known = {
        open: 'Open',
        closed: 'Closed',
        archived: 'Archived',
        hold: 'On hold',
        paused: 'Paused',
    };
    var key = s.toLowerCase();
    if (known[key])
        return known[key];
    // Title-case free-form status
    return s.replace(/[_-]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
}
exports.threadStatusLabel = threadStatusLabel;
/**
 * ng-class map / class name for thread status pill colours.
 * Returns modifier suffix classes: thread-status-pill--open | --closed | …
 */
function threadStatusPillClass(status) {
    var key = ((status || 'open').trim() || 'open').toLowerCase();
    if (key === 'open')
        return 'thread-status-pill--open';
    if (key === 'closed')
        return 'thread-status-pill--closed';
    if (key === 'archived')
        return 'thread-status-pill--archived';
    if (key === 'hold' || key === 'on hold' || key === 'on_hold')
        return 'thread-status-pill--hold';
    if (key === 'paused')
        return 'thread-status-pill--paused';
    return '';
}
exports.threadStatusPillClass = threadStatusPillClass;
/**
 * Default invoice/link role when adding to a thread.
 * Progress lives in status (see defaultMemberStatusForKind).
 */
function defaultMemberRoleForKind(_kind) {
    return 'line_item';
}
exports.defaultMemberRoleForKind = defaultMemberRoleForKind;
/** Default per-member progress when adding (work lists → todo). */
function defaultMemberStatusForKind(kind) {
    var k = (kind || 'custom').trim() || 'custom';
    if (exports.WORK_LIST_THREAD_KINDS.includes(k))
        return 'todo';
    // Invoice / box / reflection: no progress mark by default
    return 'hidden';
}
exports.defaultMemberStatusForKind = defaultMemberStatusForKind;
/** Lookup progress option by status (or legacy role value). */
function memberProgressOption(statusOrRole) {
    var r = (statusOrRole || 'todo').trim().toLowerCase();
    // Aliases → canonical
    var aliases = {
        open: 'todo',
        '0%': 'todo',
        '0': 'todo',
        in_progress: 'doing',
        wip: 'doing',
        complete: 'done',
        completed: 'done',
        closed: 'done',
        // Legacy / short forms
        testing: 'testing_local',
        test: 'testing_local',
        qa: 'testing_local',
        testing_locally: 'testing_local',
        local_test: 'testing_local',
        internal_testing: 'testing_internal',
        internal: 'testing_internal',
        deployed: 'deployed_feedback',
        deployed_for_feedback: 'deployed_feedback',
        wrap_up: 'polish',
        wrapup: 'polish',
        cleanup: 'polish',
        refactor: 'polish',
        finish: 'polish',
        bug: 'stability',
        bugfix: 'stability',
        stability_required: 'stability',
        needs_stability: 'stability',
        feedback_received: 'feedback',
        brainstorm: 'brainstorming',
        ideas: 'brainstorming',
        docs: 'documenting',
        document: 'documenting',
        documentation: 'documenting',
        sketch: 'sketching',
        sketches: 'sketching',
        proto: 'prototyping',
        prototype: 'prototyping',
        burnin: 'burn_in',
        burn_in_testing: 'burn_in',
        print: 'printing',
        exhibit: 'exhibiting',
        exhibition: 'exhibiting',
        on_show: 'exhibiting',
        housekeeping: 'ongoing',
        recurring: 'ongoing',
        repeating: 'ongoing',
        sustain: 'ongoing',
        monitor: 'monitoring',
        checks: 'monitoring',
        warrant: 'warranty',
        blocked: 'waiting',
        waiting_on: 'waiting',
        on_hold: 'paused',
        hold: 'paused',
        pause: 'paused',
        catalog: 'cataloguing',
        cataloging: 'cataloguing',
        inventory: 'cataloguing',
        critique: 'review',
        crit: 'review',
        installing: 'install',
        installation: 'install',
        '10%': 'pct10',
        '25%': 'pct25',
        '50%': 'pct50',
        '75%': 'pct75',
        '90%': 'pct90',
    };
    var key = aliases[r] || r;
    if (r === 'line_item') {
        return PROGRESS_BY_VALUE.get('line_item');
    }
    return (PROGRESS_BY_VALUE.get(key) || {
        role: r || 'todo',
        label: r || 'To do',
        mark: r ? String(r).slice(0, 4) : '0%',
        group: 'plan',
    });
}
exports.memberProgressOption = memberProgressOption;
/**
 * Accordion section id that owns a status (or invoice role) value.
 * Used to auto-expand the relevant section in the pill menu.
 */
function memberProgressSectionIdForStatus(statusOrRole) {
    var opt = memberProgressOption(statusOrRole);
    var sec = exports.MEMBER_PROGRESS_SECTIONS.find(function (s) { return s.groups.includes(opt.group); });
    return (sec === null || sec === void 0 ? void 0 : sec.id) || 'plan';
}
exports.memberProgressSectionIdForStatus = memberProgressSectionIdForStatus;
/**
 * Normalize status into a coarse check state for one-tap cycle.
 * Unknown / % map to nearest coarse step; invoice roles → todo.
 */
function memberCheckState(statusOrRole) {
    var r = (statusOrRole || 'todo').trim().toLowerCase();
    if (r === 'planning' || r === 'plan' || r === 'brainstorming' || r === 'research') {
        return 'planning';
    }
    if (r === 'started' || r === 'go' || r === 'start' || r === 'sketching')
        return 'started';
    if (r === 'doing' ||
        r === 'in_progress' ||
        r === 'wip' ||
        r === 'documenting' ||
        r === 'prototyping' ||
        r === 'ongoing' ||
        r === 'monitoring' ||
        r === 'cataloguing') {
        return 'doing';
    }
    if (r === 'testing_local' ||
        r === 'testing' ||
        r === 'test' ||
        r === 'qa' ||
        r === 'testing_locally' ||
        r === 'local_test') {
        return 'testing_local';
    }
    if (r === 'testing_internal' ||
        r === 'internal_testing' ||
        r === 'internal' ||
        r === 'burn_in' ||
        r === 'review') {
        return 'testing_internal';
    }
    if (r === 'deploying' || r === 'deploy' || r === 'printing' || r === 'install') {
        return 'deploying';
    }
    if (r === 'deployed_feedback' ||
        r === 'deployed' ||
        r === 'deployed_for_feedback' ||
        r === 'exhibiting') {
        return 'deployed_feedback';
    }
    if (r === 'polish' ||
        r === 'wrap_up' ||
        r === 'wrapup' ||
        r === 'cleanup' ||
        r === 'refactor' ||
        r === 'finish') {
        return 'polish';
    }
    if (r === 'done' || r === 'complete' || r === 'completed' || r === 'closed')
        return 'done';
    // Stability / bugfix → re-open into local testing
    if (r === 'stability' || r === 'bugfix' || r === 'bug' || r === 'needs_stability') {
        return 'testing_local';
    }
    if (r === 'feedback' || r === 'feedback_received' || r === 'warranty')
        return 'done';
    if (r === 'waiting' || r === 'paused')
        return 'todo';
    if (r.startsWith('pct') || /^\d+%$/.test(r)) {
        var n = parseInt(r.replace(/\D/g, ''), 10);
        if (n >= 90)
            return 'testing_local';
        if (n >= 50)
            return 'doing';
        if (n >= 25)
            return 'started';
        return 'todo';
    }
    // todo, hidden, line_item, open, …
    return 'todo';
}
exports.memberCheckState = memberCheckState;
/** Short badge for pill from member status (empty when hidden / no mark). */
function memberCheckMark(statusOrRole) {
    return memberProgressOption(statusOrRole).mark;
}
exports.memberCheckMark = memberCheckMark;
function memberCheckTitle(statusOrRole) {
    return memberProgressOption(statusOrRole).label;
}
exports.memberCheckTitle = memberCheckTitle;
/** Advance through coarse work-list status steps. */
function nextMemberCheckRole(statusOrRole) {
    var cur = memberCheckState(statusOrRole);
    var i = exports.MEMBER_CHECK_CYCLE.indexOf(cur);
    return exports.MEMBER_CHECK_CYCLE[(i < 0 ? 0 : i + 1) % exports.MEMBER_CHECK_CYCLE.length];
}
exports.nextMemberCheckRole = nextMemberCheckRole;
/** Prefer status; fall back to role when older cache only had progress in role. */
function memberEffectiveStatus(member) {
    var s = member.status != null ? String(member.status).trim() : '';
    if (s)
        return s;
    var r = member.role != null ? String(member.role).trim() : '';
    if (r && isMemberProgressStatus(r))
        return r;
    return 'todo';
}
exports.memberEffectiveStatus = memberEffectiveStatus;
function nowIso() {
    return new Date().toISOString();
}
function newId() {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
    }
    catch (_a) {
        /* ignore */
    }
    return "thr_".concat(Date.now().toString(36), "_").concat(Math.random().toString(36).slice(2, 10));
}
function readJsonArray(storage, key) {
    try {
        var raw = storage.getItem(key);
        if (!raw)
            return [];
        var parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    }
    catch (_a) {
        return [];
    }
}
function writeJsonArray(storage, key, value) {
    storage.setItem(key, JSON.stringify(value));
}
function normalizeMemberId(memberId) {
    return String(memberId);
}
exports.normalizeMemberId = normalizeMemberId;
/** Normalize API / local payloads into Thread (drop members if present). */
function normalizeThreadRecord(raw) {
    if (!raw || raw.id == null || raw.id === '')
        return null;
    var accountId = raw.accountId !== undefined ? raw.accountId : raw.account_id !== undefined ? raw.account_id : null;
    var createdBy = raw.createdBy != null ? raw.createdBy : raw.created_by;
    var createdAt = raw.createdAt || raw.created_at || nowIso();
    var updatedAt = raw.updatedAt || raw.updated_at || nowIso();
    var metadata = raw.metadata;
    if (metadata == null && raw.metadata_json != null) {
        try {
            metadata =
                typeof raw.metadata_json === 'string'
                    ? JSON.parse(raw.metadata_json)
                    : raw.metadata_json;
        }
        catch (_a) {
            metadata = undefined;
        }
    }
    return {
        id: String(raw.id),
        accountId: accountId !== null && accountId !== void 0 ? accountId : null,
        kind: (raw.kind || 'custom'),
        title: String(raw.title || ''),
        status: (raw.status != null && raw.status !== '' ? raw.status : 'open'),
        body: raw.body != null ? String(raw.body) : undefined,
        metadata: metadata && typeof metadata === 'object' ? metadata : undefined,
        createdBy: createdBy,
        createdAt: typeof createdAt === 'string' ? createdAt : nowIso(),
        updatedAt: typeof updatedAt === 'string' ? updatedAt : nowIso(),
    };
}
exports.normalizeThreadRecord = normalizeThreadRecord;
function normalizeMemberRecord(raw) {
    if (!raw)
        return null;
    var threadId = raw.threadId != null ? raw.threadId : raw.thread_id;
    var memberId = raw.memberId != null ? raw.memberId : raw.member_id;
    if (threadId == null || memberId == null)
        return null;
    var pathHintRaw = raw.pathHint != null ? raw.pathHint : raw.path_hint;
    var sortRaw = raw.sort;
    var sort = typeof sortRaw === 'number'
        ? sortRaw
        : sortRaw != null && String(sortRaw).trim() !== ''
            ? Number(sortRaw) || 0
            : 0;
    var roleRaw = raw.role != null && raw.role !== '' ? String(raw.role) : 'line_item';
    // Prefer explicit status / memberStatus; migrate legacy progress-in-role
    var statusRaw = raw.status != null && raw.status !== ''
        ? String(raw.status)
        : raw.memberStatus != null && raw.memberStatus !== ''
            ? String(raw.memberStatus)
            : raw.member_status != null && raw.member_status !== ''
                ? String(raw.member_status)
                : '';
    var role = roleRaw;
    if (!statusRaw && isMemberProgressStatus(roleRaw)) {
        statusRaw = roleRaw;
        role = 'line_item';
    }
    if (!statusRaw)
        statusRaw = 'todo';
    // If role still holds a progress value after status set, clear to line_item
    if (isMemberProgressStatus(role) && role !== statusRaw) {
        role = 'line_item';
    }
    return {
        threadId: String(threadId),
        memberType: (raw.memberType || raw.member_type || 'pilot_note'),
        memberId: normalizeMemberId(memberId),
        role: role,
        status: statusRaw,
        pathHint: pathHintRaw != null && pathHintRaw !== '' ? String(pathHintRaw) : undefined,
        sort: sort,
        linkedAt: raw.linkedAt || raw.linked_at || nowIso(),
        linkedBy: raw.linkedBy != null ? raw.linkedBy : raw.linked_by,
    };
}
exports.normalizeMemberRecord = normalizeMemberRecord;
/**
 * Whether a pathHint is missing or only the generic Note #id fallback.
 * Used when merging server snapshots so we do not clobber local captions.
 */
function isGenericPathHint(hint, memberId) {
    if (hint == null)
        return true;
    var t = String(hint).trim();
    if (!t)
        return true;
    var mid = String(memberId);
    return t === "Note #".concat(mid) || t === mid || t === "#".concat(mid);
}
exports.isGenericPathHint = isGenericPathHint;
/**
 * Prefer server pathHint when it is a real caption; otherwise keep a better local one.
 * Server often returns null path_hint for older memberships; open/remove refresh must not
 * wipe captions that were captured client-side at add time.
 */
function preferMemberPathHint(serverHint, localHint, memberId) {
    var s = serverHint != null ? String(serverHint).trim() : '';
    var l = localHint != null ? String(localHint).trim() : '';
    if (s && !isGenericPathHint(s, memberId))
        return s;
    if (l && !isGenericPathHint(l, memberId))
        return l;
    return s || l || undefined;
}
exports.preferMemberPathHint = preferMemberPathHint;
function memberCacheKey(m) {
    return "".concat(m.threadId, "\0").concat(m.memberType || 'pilot_note', "\0").concat(m.memberId);
}
/** Apply local pathHint when incoming (server) caption is empty/generic. */
function mergeMemberPathHints(incoming, localPrior) {
    if (!incoming.length || !localPrior.length)
        return incoming;
    var prior = new Map(localPrior.map(function (m) { return [memberCacheKey(m), m]; }));
    return incoming.map(function (m) {
        var prev = prior.get(memberCacheKey(m));
        if (!prev)
            return m;
        var pathHint = preferMemberPathHint(m.pathHint, prev.pathHint, m.memberId);
        if (pathHint === m.pathHint)
            return m;
        return __assign(__assign({}, m), { pathHint: pathHint });
    });
}
exports.mergeMemberPathHints = mergeMemberPathHints;
/**
 * Replace person-scoped local cache (server is source of truth after sync).
 * Used after GET /api/v2/threads (+ members).
 * Preserves local pathHint when server omits / only has Note #id.
 */
function replacePersonThreadCache(personId, threads, members, storage) {
    if (storage === void 0) { storage = localStorage; }
    var mKey = threadMembersStorageKey(personId);
    var priorMembers = readJsonArray(storage, mKey);
    var tList = (threads || [])
        .map(normalizeThreadRecord)
        .filter(function (t) { return !!t; });
    var mList = mergeMemberPathHints((members || []).map(normalizeMemberRecord).filter(function (m) { return !!m; }), priorMembers);
    writeJsonArray(storage, threadsStorageKey(personId), tList);
    writeJsonArray(storage, mKey, mList);
}
exports.replacePersonThreadCache = replacePersonThreadCache;
/** Upsert one thread header into local cache (create / rename / open). */
function upsertLocalThread(personId, thread, storage) {
    if (storage === void 0) { storage = localStorage; }
    var next = normalizeThreadRecord(thread);
    if (!next)
        return null;
    var key = threadsStorageKey(personId);
    var list = readJsonArray(storage, key);
    var idx = list.findIndex(function (t) { return t.id === next.id; });
    if (idx >= 0) {
        list[idx] = __assign(__assign(__assign({}, list[idx]), next), { id: next.id });
    }
    else {
        list.unshift(next);
    }
    writeJsonArray(storage, key, list);
    return next;
}
exports.upsertLocalThread = upsertLocalThread;
/** Replace all members for one thread id (keep other threads' members). */
function replaceLocalMembersForThread(personId, threadId, members, storage) {
    if (storage === void 0) { storage = localStorage; }
    if (!threadId)
        return [];
    var mKey = threadMembersStorageKey(personId);
    var existing = readJsonArray(storage, mKey);
    var priorForThread = existing.filter(function (m) { return m.threadId === threadId; });
    var others = existing.filter(function (m) { return m.threadId !== threadId; });
    var forThread = mergeMemberPathHints((members || [])
        .map(normalizeMemberRecord)
        .filter(function (m) { return !!m && m.threadId === threadId; }), priorForThread);
    writeJsonArray(storage, mKey, others.concat(forThread));
    return forThread.slice().sort(function (a, b) { return a.sort - b.sort; });
}
exports.replaceLocalMembersForThread = replaceLocalMembersForThread;
/** Upsert a single membership (add member write-through). */
function upsertLocalMember(personId, member, storage) {
    if (storage === void 0) { storage = localStorage; }
    var next = normalizeMemberRecord(member);
    if (!next)
        return null;
    var mKey = threadMembersStorageKey(personId);
    var list = readJsonArray(storage, mKey);
    var idx = list.findIndex(function (m) {
        return m.threadId === next.threadId &&
            m.memberType === next.memberType &&
            m.memberId === next.memberId;
    });
    if (idx >= 0) {
        list[idx] = next;
    }
    else {
        list.push(next);
    }
    writeJsonArray(storage, mKey, list);
    return next;
}
exports.upsertLocalMember = upsertLocalMember;
function listThreads(personId, storage) {
    if (storage === void 0) { storage = localStorage; }
    var list = readJsonArray(storage, threadsStorageKey(personId));
    return list.slice().sort(function (a, b) { return String(b.updatedAt).localeCompare(String(a.updatedAt)); });
}
exports.listThreads = listThreads;
function getThread(personId, threadId, storage) {
    if (storage === void 0) { storage = localStorage; }
    if (!threadId)
        return null;
    return listThreads(personId, storage).find(function (t) { return t.id === threadId; }) || null;
}
exports.getThread = getThread;
function createThread(personId, input, storage) {
    var _a;
    if (storage === void 0) { storage = localStorage; }
    var title = (input.title || '').trim();
    if (!title) {
        throw new Error('Thread title is required');
    }
    var kind = (input.kind || 'custom');
    var at = nowIso();
    var thread = {
        id: newId(),
        accountId: (_a = input.accountId) !== null && _a !== void 0 ? _a : null,
        kind: kind,
        title: title,
        status: input.status || 'open',
        body: input.body,
        metadata: input.metadata,
        createdBy: input.createdBy,
        createdAt: at,
        updatedAt: at,
    };
    var key = threadsStorageKey(personId);
    var list = readJsonArray(storage, key);
    list.unshift(thread);
    writeJsonArray(storage, key, list);
    return thread;
}
exports.createThread = createThread;
function updateThread(personId, threadId, patch, storage) {
    if (storage === void 0) { storage = localStorage; }
    var key = threadsStorageKey(personId);
    var list = readJsonArray(storage, key);
    var idx = list.findIndex(function (t) { return t.id === threadId; });
    if (idx < 0)
        return null;
    var next = __assign(__assign(__assign({}, list[idx]), patch), { id: list[idx].id, createdAt: list[idx].createdAt, updatedAt: nowIso() });
    if (patch.title != null) {
        next.title = String(patch.title).trim();
        if (!next.title) {
            throw new Error('Thread title is required');
        }
    }
    list[idx] = next;
    writeJsonArray(storage, key, list);
    return next;
}
exports.updateThread = updateThread;
function deleteThread(personId, threadId, storage) {
    if (storage === void 0) { storage = localStorage; }
    var tKey = threadsStorageKey(personId);
    var mKey = threadMembersStorageKey(personId);
    var threads = readJsonArray(storage, tKey);
    var nextThreads = threads.filter(function (t) { return t.id !== threadId; });
    if (nextThreads.length === threads.length)
        return false;
    writeJsonArray(storage, tKey, nextThreads);
    var members = readJsonArray(storage, mKey).filter(function (m) { return m.threadId !== threadId; });
    writeJsonArray(storage, mKey, members);
    return true;
}
exports.deleteThread = deleteThread;
function listMembers(personId, threadId, storage) {
    if (storage === void 0) { storage = localStorage; }
    var members = readJsonArray(storage, threadMembersStorageKey(personId)).filter(function (m) { return m.threadId === threadId; });
    return members.slice().sort(function (a, b) { return a.sort - b.sort || String(a.linkedAt).localeCompare(String(b.linkedAt)); });
}
exports.listMembers = listMembers;
function listMembershipsForRow(personId, rowId, storage) {
    if (storage === void 0) { storage = localStorage; }
    var mid = normalizeMemberId(rowId);
    return readJsonArray(storage, threadMembersStorageKey(personId)).filter(function (m) { return m.memberType === 'pilot_note' && m.memberId === mid; });
}
exports.listMembershipsForRow = listMembershipsForRow;
function threadsForRow(personId, rowId, storage) {
    if (storage === void 0) { storage = localStorage; }
    var memberships = listMembershipsForRow(personId, rowId, storage);
    if (!memberships.length)
        return [];
    var byId = new Map(listThreads(personId, storage).map(function (t) { return [t.id, t]; }));
    return memberships
        .map(function (m) { return byId.get(m.threadId); })
        .filter(function (t) { return !!t; });
}
exports.threadsForRow = threadsForRow;
function isMemberOfThread(personId, threadId, rowId, storage) {
    if (storage === void 0) { storage = localStorage; }
    var mid = normalizeMemberId(rowId);
    return readJsonArray(storage, threadMembersStorageKey(personId)).some(function (m) { return m.threadId === threadId && m.memberType === 'pilot_note' && m.memberId === mid; });
}
exports.isMemberOfThread = isMemberOfThread;
function addMember(personId, threadId, input, storage) {
    if (storage === void 0) { storage = localStorage; }
    var thread = getThread(personId, threadId, storage);
    if (!thread) {
        throw new Error('Thread not found');
    }
    var memberId = normalizeMemberId(input.memberId);
    if (!memberId || memberId === 'undefined' || memberId === 'null') {
        throw new Error('Member id is required');
    }
    if (isMemberOfThread(personId, threadId, memberId, storage)) {
        throw new Error('Already a member of this thread');
    }
    var mKey = threadMembersStorageKey(personId);
    var members = readJsonArray(storage, mKey);
    var maxSort = members
        .filter(function (m) { return m.threadId === threadId; })
        .reduce(function (max, m) { return Math.max(max, m.sort || 0); }, 0);
    var role = input.role || defaultMemberRoleForKind(thread.kind);
    var status = input.status != null && String(input.status).trim() !== ''
        ? String(input.status).trim()
        : defaultMemberStatusForKind(thread.kind);
    var member = {
        threadId: threadId,
        memberType: input.memberType || 'pilot_note',
        memberId: memberId,
        role: role,
        status: status,
        pathHint: input.pathHint,
        sort: maxSort + 1,
        linkedAt: nowIso(),
        linkedBy: input.linkedBy,
    };
    members.push(member);
    writeJsonArray(storage, mKey, members);
    // Touch thread updatedAt so it rises in recent lists
    updateThread(personId, threadId, {}, storage);
    return member;
}
exports.addMember = addMember;
function updateMember(personId, threadId, memberId, patch, storage) {
    if (storage === void 0) { storage = localStorage; }
    var mid = normalizeMemberId(memberId);
    var mKey = threadMembersStorageKey(personId);
    var members = readJsonArray(storage, mKey);
    var idx = members.findIndex(function (m) { return m.threadId === threadId && m.memberType === 'pilot_note' && m.memberId === mid; });
    if (idx < 0)
        return null;
    var next = __assign({}, members[idx]);
    if (patch.status !== undefined) {
        next.status = String(patch.status || '').trim() || undefined;
    }
    if (patch.role !== undefined) {
        var role = String(patch.role || '').trim();
        if (!role || role === '__removed__') {
            throw new Error('Invalid role');
        }
        // If client still patches progress via role, route to status
        if (isMemberProgressStatus(role) && !INVOICE_ROLE_VALUES.has(role)) {
            next.status = role;
        }
        else {
            next.role = role;
        }
    }
    if (patch.pathHint !== undefined) {
        next.pathHint = patch.pathHint == null || patch.pathHint === ''
            ? undefined
            : String(patch.pathHint);
    }
    members[idx] = next;
    writeJsonArray(storage, mKey, members);
    updateThread(personId, threadId, {}, storage);
    return next;
}
exports.updateMember = updateMember;
function removeMember(personId, threadId, memberId, storage) {
    if (storage === void 0) { storage = localStorage; }
    var mid = normalizeMemberId(memberId);
    var mKey = threadMembersStorageKey(personId);
    var members = readJsonArray(storage, mKey);
    var next = members.filter(function (m) { return !(m.threadId === threadId && m.memberType === 'pilot_note' && m.memberId === mid); });
    if (next.length === members.length)
        return false;
    writeJsonArray(storage, mKey, next);
    updateThread(personId, threadId, {}, storage);
    return true;
}
exports.removeMember = removeMember;
function pillsForRow(personId, rowId, storage) {
    if (storage === void 0) { storage = localStorage; }
    var memberships = listMembershipsForRow(personId, rowId, storage);
    if (!memberships.length)
        return [];
    var byId = new Map(listThreads(personId, storage).map(function (t) { return [t.id, t]; }));
    var out = [];
    for (var _i = 0, memberships_1 = memberships; _i < memberships_1.length; _i++) {
        var m = memberships_1[_i];
        var t = byId.get(m.threadId);
        if (!t)
            continue;
        var memberStatus = memberEffectiveStatus(m);
        var mark = memberCheckMark(memberStatus);
        var titleLabel = threadPillLabel(t);
        var progressTitle = memberCheckTitle(memberStatus);
        var thrStatus = (t.status || 'open');
        out.push({
            id: t.id,
            kind: t.kind,
            title: t.title,
            label: titleLabel,
            caption: "".concat(threadCaption(t), " \u00B7 ").concat(threadStatusLabel(thrStatus), " \u00B7 ").concat(progressTitle),
            status: thrStatus,
            statusLabel: threadStatusLabel(thrStatus),
            statusClass: threadStatusPillClass(thrStatus),
            role: m.role,
            memberStatus: memberStatus,
            statusMark: mark,
            statusTitle: progressTitle,
            checkMark: mark,
            checkTitle: progressTitle,
        });
    }
    return out;
}
exports.pillsForRow = pillsForRow;
/** UI helper: attach `_threadPills` on rows for Angular templates. */
function attachThreadPillsToRows(personId, rows, storage) {
    if (storage === void 0) { storage = localStorage; }
    if (!Array.isArray(rows) || !rows.length)
        return 0;
    var n = 0;
    for (var _i = 0, rows_1 = rows; _i < rows_1.length; _i++) {
        var row = rows_1[_i];
        if (!row || row.ID == null)
            continue;
        row._threadPills = pillsForRow(personId, row.ID, storage);
        n += 1;
    }
    return n;
}
exports.attachThreadPillsToRows = attachThreadPillsToRows;
/**
 * Prefer recently viewed threads (view history filtered to /view/thread/*),
 * then fall back to store order by updatedAt. Deduped by thread id.
 */
function recentThreadsForAdd(personId, rowId, viewHistoryEntries, storage, limit) {
    var _a, _b;
    if (storage === void 0) { storage = localStorage; }
    if (limit === void 0) { limit = 8; }
    var storeThreads = listThreads(personId, storage);
    var byId = new Map(storeThreads.map(function (t) { return [t.id, t]; }));
    var seen = new Set();
    var out = [];
    var pushThread = function (thread, viewedAt, source) {
        if (seen.has(thread.id))
            return;
        seen.add(thread.id);
        out.push({
            id: thread.id,
            kind: thread.kind,
            title: thread.title,
            caption: threadCaption(thread),
            label: threadPillLabel(thread),
            accountId: thread.accountId,
            alreadyMember: rowId != null && rowId !== ''
                ? isMemberOfThread(personId, thread.id, rowId, storage)
                : false,
            viewedAt: viewedAt,
            source: source,
        });
    };
    for (var _i = 0, _c = viewHistoryEntries || []; _i < _c.length; _i++) {
        var entry = _c[_i];
        var tid = (((_a = entry === null || entry === void 0 ? void 0 : entry.data) === null || _a === void 0 ? void 0 : _a.threadId) && String(entry.data.threadId)) ||
            threadIdFromViewUrl((_b = entry === null || entry === void 0 ? void 0 : entry.data) === null || _b === void 0 ? void 0 : _b.url);
        if (!tid)
            continue;
        var thread = byId.get(tid);
        if (!thread)
            continue;
        pushThread(thread, Number(entry.timestamp) || Date.now(), 'view_history');
        if (out.length >= limit)
            return out;
    }
    for (var _d = 0, storeThreads_1 = storeThreads; _d < storeThreads_1.length; _d++) {
        var thread = storeThreads_1[_d];
        pushThread(thread, Date.parse(thread.updatedAt) || 0, 'store');
        if (out.length >= limit)
            break;
    }
    return out;
}
exports.recentThreadsForAdd = recentThreadsForAdd;
